/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, ElementRef, Inject, Input, OnDestroy, OnInit, ViewChild, signal} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import * as _ from 'lodash';
import * as moment from 'moment';
import 'moment-timezone';
import * as Highcharts from 'highcharts';
import {Subscription} from 'rxjs';
import 'highcharts/modules/treemap';
import 'highcharts/modules/heatmap';
import {AdnLogService} from '../../../components/util/adn-log.service';

import template from './stats-widget.component.html';

import {StatsApiService} from '../../common/controller/stats-api.service';
import {OBJECT_TYPE} from '../../common/constants/object-type';
import {applyHighchartsLocale, chunkYValue} from './workspace-widget-chart-commons';
import {AppLocaleService} from '../../common/locale/app-locale.service';
import {getIntlLocaleData} from '../../common/locale/intl-locale-data';
import {ChartOptionsUtil} from '../../common/options/chart-options-util.service';
import {LocalNetworkProfile} from '../../../components/session/local-network-profile';
import {deferSignalStateWrite} from '../../common/util/template-state';

@Component({
  standalone: false,
  selector: 'adn-tree-map-widget',
  template
})
export class TreeMapWidgetComponent implements OnInit, OnDestroy {
  @Input() widgetType: any;

  @ViewChild('chartContainer')
  set chartContainer(chartContainer: ElementRef<HTMLElement>) {
    this.chartContainerRef = chartContainer;
    this.renderChart();
  }

  readonly chartTitle = signal('');
  readonly error = signal(false);
  readonly isPending = signal(false);
  readonly haveStats = signal(false);
  readonly noData = signal<string | null>(null);

  private chart: Highcharts.Chart | null = null;
  private chartContainerRef: ElementRef<HTMLElement> | null = null;
  private chartOptions: any = null;
  private networkTimezoneOffset = 0;
  private statsSubscription?: Subscription;

  constructor(
    private readonly statsApi: StatsApiService,
    @Inject(ChartOptionsUtil) private readonly ChartOptionsUtil: any,
    private readonly translate: TranslateService,
    private readonly adnLog: AdnLogService,
    private readonly appLocale: AppLocaleService,
    @Inject(LocalNetworkProfile) private readonly LocalNetworkProfile: any
  ) {
  }

  ngOnInit(): void {
    const timezone = this.LocalNetworkProfile.get('timeZone');
    this.networkTimezoneOffset = moment.tz(timezone).utcOffset();

    let dateOption = this.ChartOptionsUtil.getDateOptions().last7Days;
    if (this.widgetType.dateOption && this.ChartOptionsUtil.getDateOptions()[this.widgetType.dateOption]) {
      dateOption = this.ChartOptionsUtil.getDateOptions()[this.widgetType.dateOption];
    }
    const startDate = dateOption.startDate();
    const endDate = dateOption.endDate();

    const networkTransFieldName = this.LocalNetworkProfile.getNetworkTranslations(this.widgetType.selField);
    const fieldName = networkTransFieldName || this.translate.instant('report.field.' + this.widgetType.selField);
    const networkTransField2Name = this.LocalNetworkProfile.getNetworkTranslations(this.widgetType.selField2);
    const field2Name = this.widgetType.selField2
      ? networkTransField2Name || this.translate.instant('report.field.' + this.widgetType.selField2)
      : '';

    let chartTitle = networkTransFieldName || fieldName;
    if (field2Name) {
      chartTitle += ' and ' + (networkTransField2Name || field2Name);
    }
    this.chartTitle.set(chartTitle + ' for ' + this.translate.instant('chart.dateOption.' + dateOption.id + '.sentence'));

    const ids = _.map(this.widgetType.objIds, 'id');
    if (!ids.length) {
      this.isPending.set(false);
      this.noData.set('Select at least one object to report stats on.');
      return;
    }

    const objType = OBJECT_TYPE[this.widgetType.objectType] || OBJECT_TYPE.LineItem;
    const chartType = this.ChartOptionsUtil.getOptions(objType.caps);

    this.chartOptions = {
      colorAxis: {
        min: 0,
        minColor: '#fafaff',
        maxColor: Highcharts.getOptions().colors[0]
      },
      chart: {
        type: 'treemap',
        height: this.widgetType.height || 300
      },
      tooltip: {
        formatter: function() {
          let tip = this.point.name + '<br>' + this.point.fieldName + ': <strong>' + this.point.value + '</strong>';
          if (this.point.field2Name) {
            tip += '<br>' + this.point.field2Name + ': <strong>' + this.point.colorValue + '</strong>';
          }
          return tip;
        }
      },
      series: [{
        type: 'treemap',
        data: []
      }],
      title: {text: null},
      credits: {enabled: false},
      exporting: {enabled: true},
      accessibility: {enabled: false},
      legend: {enabled: !!field2Name}
    };

    const statsParams: any = {
      id: _.slice(ids, 0, 15).join(';'),
      idKey: chartType.idKey,
      startDate,
      endDate,
      groupBy: objType.caps
    };

    this.statsSubscription = this.statsApi.get(statsParams).subscribe({next: (stats: any) => {
      applyHighchartsLocale(getIntlLocaleData(this.appLocale.getLocaleId()), this.networkTimezoneOffset);
      this.error.set(false);

      this.chartOptions.series[0].data = _.map(stats.chunks, (chunk: any) => {
        const value = chunkYValue(chunk[this.widgetType.selField]) || 0;
        const chartsData: any = {
          name: chunk[objType.apiParam].name,
          fieldName,
          value
        };
        if (field2Name) {
          chartsData.field2Name = field2Name;
          chartsData.colorValue = chunkYValue(chunk[this.widgetType.selField2]) || 0;
        }
        return chartsData;
      });
      this.chartOptions.colorAxis.max = chunkYValue(stats.totals[this.widgetType.selField2]);

      const noData = !stats.chunks.length
        ? 'No data was found for your selected '
          + this.translate.instant('object.' + this.widgetType.objectType + '.plural').toLowerCase()
        : null;
      deferSignalStateWrite(() => {
        this.noData.set(noData);
        this.haveStats.set(true);
        this.isPending.set(false);
        this.renderChart();
      });
    }, error: (data: any) => {
      this.adnLog.warn(data);
      deferSignalStateWrite(() => {
        if (data.status !== -1) {
          this.error.set(true);
        }
        this.isPending.set(false);
      });
    }});
  }

  ngOnDestroy(): void {
    this.statsSubscription?.unsubscribe();
    this.chart?.destroy();
    this.chart = null;
  }

  private renderChart(): void {
    if (!this.chartContainerRef?.nativeElement || !this.chartOptions || !this.haveStats() || this.noData()) {
      return;
    }
    this.chart?.destroy();
    this.chart = Highcharts.chart(this.chartContainerRef.nativeElement, this.chartOptions);
  }
}
